import { apiUrl, csrfTokenCookie, type SessionRole } from '@/lib/api-client'
import type { UserProfile } from '@/lib/types'
import { trustedRedirect, type AuthConfig } from '@/features/auth/auth-config'
import { noteViewAsResponse } from '@/lib/view-as-sync'

/**
 * The cookie session of an Edvantage ID sign-in, as the browser sees it (spec 5.1, 5.4). The API answers these routes with
 * plain JSON rather than the `/mocktest` envelope, so they are read here and not through `apiRequest`.
 */

/**
 * A superadmin using this product as someone else (VIEW-AS-SPEC 2.6). `expires_at` is when the session ends for good (30 minutes
 * after it started, never extended); `return_url` is the person's page in Edvantage ID, where Exit goes.
 */
export interface Impersonation {
  actor_name: string
  target_name: string
  expires_at: string
  return_url: string
}

export type OidcSessionResult =
  | { kind: 'session'; role: SessionRole; profile: UserProfile; impersonation: Impersonation | null; canViewAs: boolean }
  | { kind: 'signed-out' }
  | { kind: 'unavailable' }

const ROLES: readonly SessionRole[] = ['student', 'admin', 'teacher']

function record(value: unknown): Record<string, unknown> {
  return value && typeof value === 'object' ? (value as Record<string, unknown>) : {}
}

function str(value: unknown): string {
  return typeof value === 'string' ? value : ''
}

/**
 * The profile every page reads (`profile.user.role` decides the router). The server's student and employee profiles already
 * carry it; the role is set from the session's own `role` so a profile that omits it can never leave the router guessing.
 * A teacher's profile is `{name, email, school}`.
 */
export function profileFromSession(role: SessionRole, raw: unknown): UserProfile {
  const profile = record(raw)
  if (role === 'teacher') {
    const school = record(profile.school)
    const name = str(profile.name)
    const email = str(profile.email)
    const [first = '', ...rest] = name.split(' ')
    return {
      id: email || name,
      first_name: first,
      last_name: rest.join(' '),
      full_name: name,
      email,
      user: { id: email || name, email, role },
      school: { id: str(school.id), name: str(school.name) },
    }
  }
  const user = record(profile.user)
  return {
    ...(profile as unknown as UserProfile),
    user: { ...(user as unknown as UserProfile['user']), role },
  }
}

/** The impersonation the API reports, or null. Both names and a parseable end time are needed to show the bar honestly. */
export function parseImpersonation(raw: unknown): Impersonation | null {
  const value = record(raw)
  const target = str(value.target_name)
  if (!target || Number.isNaN(Date.parse(str(value.expires_at)))) return null
  return { actor_name: str(value.actor_name), target_name: target, expires_at: str(value.expires_at), return_url: str(value.return_url) }
}

export async function fetchOidcSession(): Promise<OidcSessionResult> {
  try {
    const response = await fetch(apiUrl('/auth/session/'), {
      credentials: 'include',
      headers: { Accept: 'application/json' },
      signal: AbortSignal.timeout(8_000),
    })
    // The boot answer is the baseline a tab compares later answers with (view-as-sync).
    noteViewAsResponse(response)
    if (response.status === 401 || response.status === 403 || response.status === 404) return { kind: 'signed-out' }
    if (!response.ok) return { kind: 'unavailable' }
    const body = record(await response.json())
    const inner = body.role === undefined && body.data ? record(body.data) : body
    const role = ROLES.find((candidate) => candidate === inner.role)
    if (!role) return { kind: 'signed-out' }
    return {
      kind: 'session',
      role,
      profile: profileFromSession(role, inner.profile),
      impersonation: parseImpersonation(inner.impersonation),
      canViewAs: inner.can_view_as === true,
    }
  } catch {
    return { kind: 'unavailable' }
  }
}

const LOGOUT_TIMEOUT_MS = 4_000

/**
 * Sign out of Edvantage ID: the API revokes the refresh token and answers where to go to end the Edvantage ID session
 * (`{redirect}`). Resolves null when the call failed or the redirect is not one we trust; the caller has already cleared the
 * local session, so a failure only means the page stays on the sign-in screen (IELTS-506: sign-out never waits on the server).
 */
export async function oidcLogout(role: SessionRole, config: AuthConfig | null): Promise<string | null> {
  const path = role === 'teacher' ? '/school/auth/logout/' : '/auth/oidc/logout'
  const headers: Record<string, string> = { Accept: 'application/json' }
  const csrf = csrfTokenCookie()
  if (csrf) headers['X-CSRFToken'] = csrf
  try {
    const response = await fetch(apiUrl(path), {
      method: 'POST',
      credentials: 'include',
      headers,
      signal: AbortSignal.timeout(LOGOUT_TIMEOUT_MS),
    })
    if (!response.ok) return null
    const body = record(await response.json())
    const inner = body.redirect === undefined && body.data ? record(body.data) : body
    return trustedRedirect(inner.redirect, config)
  } catch {
    return null
  }
}

const EXIT_TIMEOUT_MS = 6_000

/**
 * End a View as session (VIEW-AS-SPEC 2.4): the API revokes it, clears this product's cookies and answers where to go, the
 * person's page in Edvantage ID (`{redirect}`). Resolves null when the call failed or the address is not one we trust; the
 * caller then goes to `/`, which signs the superadmin in again as themselves.
 */
export async function requestViewAsExit(config: AuthConfig | null): Promise<string | null> {
  const headers: Record<string, string> = { Accept: 'application/json' }
  const csrf = csrfTokenCookie()
  if (csrf) headers['X-CSRFToken'] = csrf
  try {
    const response = await fetch(apiUrl('/auth/view-as/exit'), {
      method: 'POST',
      credentials: 'include',
      headers,
      signal: AbortSignal.timeout(EXIT_TIMEOUT_MS),
    })
    if (!response.ok) return null
    const body = record(await response.json())
    const inner = body.redirect === undefined && body.data ? record(body.data) : body
    return trustedRedirect(inner.redirect, config)
  } catch {
    return null
  }
}
